import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CalendarOff, Plus, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import ProviderTabs from '../components/ProviderTabs';
import LoadingScreen from '../components/LoadingScreen';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REASON_OPTIONS = [
  { value: 'Mantenimiento',    icon: '🔧' },
  { value: 'Uso personal',     icon: '👤' },
  { value: 'Revisión técnica', icon: '⚙️' },
  { value: 'Limpieza',         icon: '🧹' },
  { value: 'Reservado',        icon: '🔒' },
  { value: 'Otro',             icon: '📝' },
];

const today = new Date().toISOString().split('T')[0];

function fmt(iso) {
  try {
    const [year, month, day] = iso.split('T')[0].split('-');
    return new Date(year, month - 1, day).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export default function ProviderAvailability() {
  const { t } = useTranslation();
  const { assetId } = useParams();

  const [asset, setAsset] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ start: '', end: '', reason: 'Mantenimiento' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (type, msg) => { setToast({ type, msg }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${BACKEND_URL}/api/assets/${assetId}`).then(r => (r.ok ? r.json() : null)),
      fetch(`${BACKEND_URL}/api/provider/assets/${assetId}/blocked-dates`, { credentials: 'include' })
        .then(r => (r.ok ? r.json() : [])),
    ])
      .then(([a, b]) => {
        setAsset(a);
        setBlocks(Array.isArray(b) ? b : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [assetId]);

  useEffect(() => { load(); }, [load]);

  const addBlock = async () => {
    setError('');
    if (!form.start || !form.end) { setError('Indica fecha de inicio y fin.'); return; }
    if (form.end <= form.start) { setError('La fecha de fin debe ser posterior a la de inicio.'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/provider/assets/${assetId}/blocked-dates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: `${form.start}T00:00:00Z`,
          end_date: `${form.end}T23:59:00Z`,
          reason: form.reason,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'No se pudo crear el bloqueo.'); return; }
      setForm({ start: '', end: '', reason: 'Mantenimiento' });
      showToast('success', 'Fechas bloqueadas');
      load();
    } catch {
      setError('Error de conexión.');
    } finally {
      setSaving(false);
    }
  };

  const removeBlock = async (blockId) => {
    // Confirmación antes del DELETE — antes se eliminaba de un click, con riesgo
    // de que el proveedor liberara sin querer fechas que quería mantener bloqueadas.
    if (!window.confirm(t('providerAvailability.confirmDelete', '¿Eliminar este bloqueo de disponibilidad?'))) {
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/provider/blocked-dates/${blockId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        setBlocks(prev => prev.filter(b => b.block_id !== blockId));
        showToast('success', 'Bloqueo eliminado');
      }
    } catch {
      showToast('error', 'Error de conexión');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <ProviderTabs />
        <LoadingScreen inline className="min-h-[60vh]" message="Cargando disponibilidad..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <ProviderTabs />

      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-2xl shadow-xl text-white font-medium text-sm ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-500'}`}>
          {toast.msg}
        </div>
      )}

      <div className="container mx-auto px-4 md:px-6 max-w-3xl py-8 pb-24 md:pb-8">
        <BackButton to="/provider/assets" label={t('providerAvailability.backToAssets', 'Volver a mis vehículos')} className="mb-4" />

        <div className="flex items-center gap-3 mb-1">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#36C0C7] to-[#134168] flex items-center justify-center shrink-0">
            <CalendarOff className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-[#111827]">Disponibilidad</h1>
            <p className="text-sm text-gray-500">{asset?.name || 'Vehículo'}</p>
          </div>
        </div>

        <p className="text-sm text-gray-500 mt-3 mb-6">
          Bloquea los días en los que este vehículo no estará disponible (mantenimiento, uso personal, etc.).
          Los clientes no podrán reservar dentro de esos rangos.
        </p>

        {/* Formulario de nuevo bloqueo */}
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-6">
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><Plus className="w-5 h-5 text-[#134168]" /> Bloquear fechas</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Desde</label>
              <input
                type="date" value={form.start} min={today}
                onChange={e => setForm({ ...form, start: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Hasta</label>
              <input
                type="date" value={form.end} min={form.start || today}
                onChange={e => setForm({ ...form, end: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
              />
            </div>
          </div>

          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Motivo</label>
          <div className="flex flex-wrap gap-2 mb-4">
            {REASON_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setForm({ ...form, reason: opt.value })}
                className={`px-3 py-2 rounded-full text-sm font-medium border transition ${
                  form.reason === opt.value
                    ? 'bg-[#134168] text-white border-[#134168]'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {opt.icon} {opt.value}
              </button>
            ))}
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2 mb-4">
              <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
            </div>
          )}

          <button
            onClick={addBlock}
            disabled={saving}
            className="w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-[#36C0C7] to-[#134168] text-white font-bold shadow-md hover:scale-[1.02] transition-transform disabled:opacity-60"
          >
            {saving ? 'Guardando...' : 'Bloquear estas fechas'}
          </button>
        </div>

        {/* Lista de bloqueos */}
        <h2 className="font-bold text-lg mb-3">Fechas bloqueadas</h2>
        {blocks.length === 0 ? (
          <div className="bg-white rounded-3xl p-10 shadow-sm border border-gray-100 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-gray-500">No hay fechas bloqueadas. El vehículo está disponible siempre.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {blocks.map(b => (
              <div key={b.block_id} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-lg shrink-0">
                    {(REASON_OPTIONS.find(o => o.value === b.reason) || { icon: '🔒' }).icon}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{fmt(b.start_date)} → {fmt(b.end_date)}</p>
                    <p className="text-xs text-gray-500">{b.reason || 'Bloqueado'}</p>
                  </div>
                </div>
                <button
                  onClick={() => removeBlock(b.block_id)}
                  title="Eliminar bloqueo"
                  className="p-2 rounded-full hover:bg-red-50 transition shrink-0"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
